import type { Graphics } from 'pixi.js';
import { TAU } from '../paint';

const OUT = 0x040612;
const CURL_STEPS = 14;
const OUTLINE = 1.1;
const LINE = 0.6;

export interface SilkColors {
  readonly band: number;
  readonly bandLine: number;
  readonly light: number;
  readonly lightLine: number;
}

export class Silk {
  private readonly xs: Float64Array;
  private readonly ys: Float64Array;
  private readonly widths: Float64Array;
  private readonly nx: Float64Array;
  private readonly ny: Float64Array;
  private count = 0;

  constructor(points: number) {
    const most = points + CURL_STEPS;
    this.xs = new Float64Array(most);
    this.ys = new Float64Array(most);
    this.widths = new Float64Array(most);
    this.nx = new Float64Array(most);
    this.ny = new Float64Array(most);
  }

  begin(): void {
    this.count = 0;
  }

  push(x: number, y: number, width: number): void {
    if (this.count >= this.xs.length) {
      return;
    }
    this.xs[this.count] = x;
    this.ys[this.count] = y;
    this.widths[this.count] = width;
    this.count += 1;
  }

  curl(turn: number, radius: number, turns: number, end: number): void {
    const last = this.count - 1;
    if (last < 2) {
      return;
    }
    const ex = this.xs[last] ?? 0;
    const ey = this.ys[last] ?? 0;
    const dx = ex - (this.xs[last - 2] ?? 0);
    const dy = ey - (this.ys[last - 2] ?? 0);
    const length = Math.hypot(dx, dy) || 1;
    const cx = ex - (dy / length) * turn * radius;
    const cy = ey + (dx / length) * turn * radius;
    const start = Math.atan2(ey - cy, ex - cx);
    for (let step = 1; step <= CURL_STEPS; step += 1) {
      const share = step / CURL_STEPS;
      const angle = start + turn * share * turns * TAU;
      const r = radius * (1 - 0.72 * share);
      this.push(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r, end * (1 - 0.8 * share));
    }
  }

  draw(g: Graphics, side: number, colors: SilkColors, alpha: number): void {
    if (this.count < 2 || alpha <= 0) {
      return;
    }
    this.normals();
    this.band(g, 0, 1, colors.band, colors.bandLine, 0.25, 0.45, alpha);
    this.band(g, -side * 0.22, 0.5, colors.light, colors.lightLine, 0.35, 0.7, alpha);
  }

  private normals(): void {
    const { xs, ys, nx, ny, count } = this;
    for (let index = 0; index < count; index += 1) {
      const ahead = Math.min(index + 1, count - 1);
      const behind = Math.max(index - 1, 0);
      const dx = (xs[ahead] ?? 0) - (xs[behind] ?? 0);
      const dy = (ys[ahead] ?? 0) - (ys[behind] ?? 0);
      const length = Math.hypot(dx, dy) || 1;
      nx[index] = -dy / length;
      ny[index] = dx / length;
    }
  }

  private edge(index: number, offset: number, axis: Float64Array, normal: Float64Array): number {
    return (axis[index] ?? 0) + (normal[index] ?? 0) * (this.widths[index] ?? 0) * offset;
  }

  private band(
    g: Graphics,
    shift: number,
    size: number,
    fill: number,
    line: number,
    first: number,
    second: number,
    alpha: number,
  ): void {
    const { xs, ys, nx, ny, count } = this;
    const out = shift + size / 2;
    const off = shift - size / 2;
    g.moveTo(this.edge(0, out, xs, nx), this.edge(0, out, ys, ny));
    for (let index = 1; index < count; index += 1) {
      g.lineTo(this.edge(index, out, xs, nx), this.edge(index, out, ys, ny));
    }
    for (let index = count - 1; index >= 0; index -= 1) {
      g.lineTo(this.edge(index, off, xs, nx), this.edge(index, off, ys, ny));
    }
    g.closePath()
      .fill({ color: fill, alpha })
      .stroke({ width: OUTLINE, color: OUT, alpha: 0.9 * alpha, join: 'round' });
    this.stripe(g, out + (off - out) * first, line, alpha);
    this.stripe(g, out + (off - out) * second, line, alpha);
  }

  private stripe(g: Graphics, at: number, line: number, alpha: number): void {
    const { xs, ys, nx, ny, count } = this;
    g.moveTo(this.edge(0, at, xs, nx), this.edge(0, at, ys, ny));
    for (let index = 1; index < count; index += 1) {
      g.lineTo(this.edge(index, at, xs, nx), this.edge(index, at, ys, ny));
    }
    g.stroke({ width: LINE, color: line, alpha, cap: 'round', join: 'round' });
  }
}
